import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import HtmlDocumentEditor from '../../../shared/ui/HtmlDocumentEditor';

function fn(name: string) {
  return httpsCallable(getFunctions(), name);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function mergePreview(
  html: string,
  values: Record<string, string>,
  signatureUrl?: string | null
): string {
  return String(html || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key: string) => {
    if (key === 'overtureSignature') {
      return signatureUrl
        ? `<img src="${escapeHtml(signatureUrl)}" alt="Overture signature" />`
        : '<span style="display:inline-block;width:180px;border-bottom:1px solid #111;height:32px"></span>';
    }
    if (key === 'employeeSignature') {
      return '<span style="display:inline-block;width:180px;border-bottom:1px solid #111;height:32px"></span>';
    }
    return escapeHtml(values[key] || `{{${key}}}`);
  });
}

export default function AdminHiringTemplatesPage() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [offerHtml, setOfferHtml] = useState('');
  const [contractHtml, setContractHtml] = useState('');
  const [defaultHourlyRate, setDefaultHourlyRate] = useState('70');
  const [defaultAideRate, setDefaultAideRate] = useState('30');
  const [defaultLessonFee, setDefaultLessonFee] = useState('70');
  const [defaultTrainingFee, setDefaultTrainingFee] = useState('30');
  const [offerDeadlineDays, setOfferDeadlineDays] = useState(7);
  const [onboardingDueDays, setOnboardingDueDays] = useState(14);
  const [overtureSignature, setOvertureSignature] = useState<any>(null);
  const [overtureSignerName, setOvertureSignerName] = useState('Aspen and Steven');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await fn('hiringAdminGetTemplates')({});
      const d = res.data || {};
      setOfferHtml(String(d.offerHtml || ''));
      setContractHtml(String(d.contractHtml || ''));
      setDefaultHourlyRate(String(d.defaultHourlyRate || '70'));
      setDefaultAideRate(String(d.defaultAideRate || '30'));
      setDefaultLessonFee(String(d.defaultLessonFee || '70'));
      setDefaultTrainingFee(String(d.defaultTrainingFee || '30'));
      setOfferDeadlineDays(Number(d.offerDeadlineDays) || 7);
      setOnboardingDueDays(Number(d.onboardingDueDays) || 14);
      setOvertureSignature(d.overtureSignature || null);
      setOvertureSignerName(String(d.overtureSignerName || 'Aspen and Steven'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load hiring templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await fn('hiringAdminSaveTemplates')({
        offerHtml,
        contractHtml,
        defaultHourlyRate,
        defaultAideRate,
        defaultLessonFee,
        defaultTrainingFee,
        offerDeadlineDays,
        onboardingDueDays,
        overtureSignature,
        overtureSignerName,
      });
      setInfo('Hiring templates saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const uploadSignature = async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      void window.appAlert('Please upload a PNG or JPG signature image.');
      return;
    }
    const ext = file.name.split('.').pop() || 'png';
    const path = `hiringTemplates/overture-signature.${ext}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, file, { contentType: file.type });
    const url = await getDownloadURL(r);
    setOvertureSignature({
      storagePath: path,
      url,
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      uploadedAt: new Date().toISOString(),
      uploadedBy: userData?.id || '',
    });
    setInfo('Signature uploaded — click Save to persist.');
  };

  const previewValues = useMemo(
    () => ({
      employeeName: 'Sample Teacher',
      overtureSignerName: overtureSignerName || 'Aspen and Steven',
      startDate: 'August 24, 2026',
      effectiveDate: 'August 24, 2026',
      hourlyRate: defaultHourlyRate,
      aideRate: defaultAideRate,
      lessonFee: defaultLessonFee,
      trainingFee: defaultTrainingFee,
      overtureSignDate: 'August 21, 2026',
      employeeSignDate: 'August 21, 2026',
    }),
    [
      overtureSignerName,
      defaultHourlyRate,
      defaultAideRate,
      defaultLessonFee,
      defaultTrainingFee,
    ]
  );

  const offerPreviewHtml = useMemo(
    () => mergePreview(offerHtml, previewValues, overtureSignature?.url),
    [offerHtml, previewValues, overtureSignature?.url]
  );
  const contractPreviewHtml = useMemo(
    () => mergePreview(contractHtml, previewValues, overtureSignature?.url),
    [contractHtml, previewValues, overtureSignature?.url]
  );

  if (loading) {
    return <div style={{ padding: 24 }}>Loading hiring templates…</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div>
          <button type="button" onClick={() => navigate('/app/admin')} style={{ marginBottom: 8 }}>
            ← Admin
          </button>
          <h1 style={{ margin: 0 }}>Hiring document templates</h1>
          <p style={{ color: '#64748b', marginTop: 6 }}>
            Edit the offer letter and employment agreement like a document. Use Preview to see the
            letter-sized layout with sample hiring details.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{
            background: '#0288D1',
            color: '#fff',
            border: 0,
            borderRadius: 10,
            padding: '10px 16px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save templates'}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 8 }}>
          {error}
        </div>
      )}
      {info && (
        <div style={{ marginTop: 12, padding: 12, background: '#ecfdf5', color: '#065f46', borderRadius: 8 }}>
          {info}
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        {[
          ['Hourly rate', defaultHourlyRate, setDefaultHourlyRate],
          ['Aide rate', defaultAideRate, setDefaultAideRate],
          ['Lesson fee', defaultLessonFee, setDefaultLessonFee],
          ['Learning session fee', defaultTrainingFee, setDefaultTrainingFee],
        ].map(([label, value, setter]: any) => (
          <label key={label} style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            <span>{label}</span>
            <input
              value={value}
              onChange={(e) => setter(e.target.value)}
              style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }}
            />
          </label>
        ))}
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          <span>Offer deadline (days)</span>
          <input
            type="number"
            min={1}
            value={offerDeadlineDays}
            onChange={(e) => setOfferDeadlineDays(Number(e.target.value) || 7)}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          <span>Onboarding due (days)</span>
          <input
            type="number"
            min={1}
            value={onboardingDueDays}
            onChange={(e) => setOnboardingDueDays(Number(e.target.value) || 14)}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }}
          />
        </label>
      </div>

      <div style={{ marginTop: 16, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <strong>Overture signature block</strong>
        <div style={{ marginTop: 8, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {overtureSignature?.url ? (
            <img src={overtureSignature.url} alt="Overture signature" style={{ height: 56, background: '#fff' }} />
          ) : (
            <span style={{ color: '#64748b' }}>No signature uploaded yet</span>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadSignature(f).catch((err) => void window.appAlert(err?.message || 'Upload failed'));
            }}
          />
          <label style={{ display: 'grid', gap: 4, minWidth: 240, fontSize: 13 }}>
            <span>Name shown beneath “By”</span>
            <input
              value={overtureSignerName}
              onChange={(e) => setOvertureSignerName(e.target.value)}
              placeholder="Aspen and Steven"
              style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }}
            />
          </label>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <HtmlDocumentEditor
          label="Offer letter"
          value={offerHtml}
          onChange={setOfferHtml}
          minHeight={760}
          previewHtml={offerPreviewHtml}
        />
      </div>
      <div style={{ marginTop: 20 }}>
        <HtmlDocumentEditor
          label="Employment agreement"
          value={contractHtml}
          onChange={setContractHtml}
          minHeight={900}
          previewHtml={contractPreviewHtml}
        />
      </div>
    </div>
  );
}
